import { useEffect, useState } from "react";
import { toast } from "sonner";
import { FloppyDisk, Sparkle } from "@phosphor-icons/react";
import { AppHeader } from "@/components/AppHeader";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { api, API, formatApiErrorDetail } from "@/lib/api";

const MODEL_LABELS = {
  "gemini-3.1-flash-image-preview": "Gemini Nano Banana — flash (fast, best for stone)",
  "gemini-3-pro-image-preview": "Gemini Nano Banana — pro (slower, most detail)",
  "gpt-image-1": "OpenAI GPT Image 1 (no slab photo reference)",
};

const Row = ({ label, hint, children }) => (
  <div className="flex flex-wrap items-center justify-between gap-3 border-b border-zinc-800 py-4 last:border-0">
    <div className="min-w-0">
      <div className="text-sm font-bold text-zinc-200">{label}</div>
      {hint && <div className="mt-0.5 text-xs text-zinc-500">{hint}</div>}
    </div>
    {children}
  </div>
);

const Num = ({ id, value, onChange }) => (
  <Input
    data-testid={`ai-${id}`}
    type="number"
    min="0"
    value={value}
    onChange={(e) => onChange(Number(e.target.value) || 0)}
    className="h-10 w-28 border-zinc-800 bg-zinc-950 text-sm"
  />
);

export default function AiImagesSettings() {
  const [cfg, setCfg] = useState(null);
  const [usage, setUsage] = useState({});
  const [keyReady, setKeyReady] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [shot, setShot] = useState("");

  useEffect(() => {
    api
      .get("/settings/ai-images")
      .then(({ data }) => {
        setCfg(data.config);
        setUsage(data.usage || {});
        setKeyReady(data.key_ready);
      })
      .catch(() => toast.error("Could not load the image settings"));
  }, []);

  const set = (patch) => setCfg((c) => ({ ...c, ...patch }));

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/settings/ai-images", cfg);
      setCfg(data.config);
      setUsage(data.usage || {});
      toast.success("Saved");
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err.response?.data?.detail) || "Could not save",
      );
    }
    setSaving(false);
  };

  const testShot = async () => {
    setTesting(true);
    setShot("");
    try {
      const { data } = await api.post("/ai-images/generate", {
        prompt:
          "A kitchen island topped with a dramatic white and grey marble, warm oak cabinets, morning light",
        purpose: "test",
      });
      setShot(data.item.path);
      setUsage(data.usage || {});
      toast.success("Picture generated");
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err.response?.data?.detail) ||
          "Could not generate a picture",
      );
    }
    setTesting(false);
  };

  if (!cfg)
    return (
      <div className="grain min-h-screen">
        <AppHeader title="AI Images" />
        <div className="h-40 animate-pulse rounded-lg border border-zinc-800 bg-zinc-900" />
      </div>
    );

  return (
    <div className="grain min-h-screen">
      <AppHeader title="AI Images" />
      <main className="relative z-10 pb-28 pt-6" data-testid="ai-images-page">
        <div className="text-xs uppercase tracking-widest text-zinc-600">
          Settings
        </div>
        <h1 className="text-2xl font-bold tracking-tight sm:text-3xl">
          AI Images
        </h1>
        <p className="mt-1 max-w-2xl text-sm text-zinc-500">
          Room mockups for customers, sales renders on quotes and website
          pictures — all generated from your own slab photos. Switch it off any
          time to stop spending.
        </p>

        {!keyReady && (
          <div
            className="mt-4 rounded-lg border border-amber-500/40 bg-amber-500/10 p-4 text-sm text-amber-200"
            data-testid="ai-key-warning"
          >
            No image key is configured on the server yet, so generation will
            fail.
          </div>
        )}

        <div className="mt-6 grid gap-4 lg:grid-cols-[1.4fr_1fr] lg:items-start">
          <div className="min-w-0 space-y-4">
            <section
              className="rounded-lg border border-zinc-800 bg-zinc-900 p-5"
              data-testid="ai-switches"
            >
              <Row
                label="Image generation"
                hint="Master switch — off means nothing can be generated anywhere"
              >
                <Switch
                  data-testid="ai-enabled"
                  checked={cfg.enabled}
                  onCheckedChange={(v) => set({ enabled: v })}
                />
              </Row>
              <Row
                label="Customer room visualiser"
                hint="Shoppers pick a room on a slab page and see that stone in it"
              >
                <Switch
                  data-testid="ai-visualiser"
                  checked={cfg.visualiser}
                  onCheckedChange={(v) => set({ visualiser: v })}
                />
              </Row>
              <Row
                label="Customer photo upload"
                hint="Shoppers can upload a photo of their own kitchen or bath and see this stone on their counters"
              >
                <Switch
                  data-testid="ai-photo-upload"
                  checked={cfg.photo_upload !== false}
                  onCheckedChange={(v) => set({ photo_upload: v })}
                />
              </Row>
              <Row
                label="Instant estimate"
                hint="A ballpark price on the slab page from your pricing settings (no AI)"
              >
                <Switch
                  data-testid="ai-instant-quote"
                  checked={cfg.instant_quote !== false}
                  onCheckedChange={(v) => set({ instant_quote: v })}
                />
              </Row>
              <Row
                label="Staff tools"
                hint="Quote mockups and the Generate button on website pictures"
              >
                <Switch
                  data-testid="ai-staff-tools"
                  checked={cfg.staff_tools}
                  onCheckedChange={(v) => set({ staff_tools: v })}
                />
              </Row>
              <Row label="Model" hint="Nano Banana can copy your slab's veining">
                <select
                  data-testid="ai-model"
                  value={cfg.model}
                  onChange={(e) => set({ model: e.target.value })}
                  className="h-10 max-w-full rounded-md border border-zinc-800 bg-zinc-950 px-3 text-sm text-zinc-200"
                >
                  {Object.entries(MODEL_LABELS).map(([id, label]) => (
                    <option key={id} value={id}>
                      {label}
                    </option>
                  ))}
                </select>
              </Row>
            </section>

            <section
              className="rounded-lg border border-zinc-800 bg-zinc-900 p-5"
              data-testid="ai-enhance"
            >
              <div className="text-xs font-bold uppercase tracking-widest text-zinc-300">
                HD photo enhancement
              </div>
              <p className="mt-1 text-xs text-zinc-500">
                Every slab, remnant, sink, job and completion photo is upscaled,
                sharpened, de-noised and color-corrected by Nano Banana after it
                uploads. The camera original is kept, so any photo can be put
                back with one click.
              </p>
              <Row
                label="Enhance new uploads automatically"
                hint="Off = staff use the Enhance HD button in the photo editor instead"
              >
                <Switch
                  data-testid="ai-auto-enhance"
                  checked={cfg.auto_enhance !== false}
                  onCheckedChange={(v) => set({ auto_enhance: v })}
                />
              </Row>
              <Row
                label="Output size"
                hint="Photos bigger than the size you pick are enhanced at 4K so no detail is lost"
              >
                <select
                  data-testid="ai-enhance-size"
                  value={cfg.enhance_size || "2K"}
                  onChange={(e) => set({ enhance_size: e.target.value })}
                  className="h-10 max-w-full rounded-md border border-zinc-800 bg-zinc-950 px-3 text-sm text-zinc-200"
                >
                  <option value="2K">2K — ~2048 px, sharp on any screen</option>
                  <option value="4K">4K — ~4096 px, zoom into the veining</option>
                </select>
              </Row>
              <Row
                label="Clean-up"
                hint="Job and completion photos always use Faithful — rooms are never altered"
              >
                <select
                  data-testid="ai-enhance-mode"
                  value={cfg.enhance_mode || "faithful"}
                  onChange={(e) => set({ enhance_mode: e.target.value })}
                  className="h-10 max-w-full rounded-md border border-zinc-800 bg-zinc-950 px-3 text-sm text-zinc-200"
                >
                  <option value="faithful">
                    Faithful — keep the yard, fix quality only
                  </option>
                  <option value="clean">
                    Faithful + neat backdrop — remove the yard, straighten
                  </option>
                </select>
              </Row>
              <p className="mt-3 text-[11px] text-zinc-500">
                Each HD pass is one picture against the daily and monthly limits
                below (2K ≈ 15 s, 4K ≈ 30 s). Raise the caps if you photograph a
                lot of stock in one day.
              </p>
            </section>

            <section
              className="rounded-lg border border-zinc-800 bg-zinc-900 p-5"
              data-testid="ai-limits"
            >
              <div className="text-xs font-bold uppercase tracking-widest text-zinc-300">
                Usage limits
              </div>
              <Row label="Pictures per day" hint="Everything, staff and public">
                <Num
                  id="daily-limit"
                  value={cfg.daily_limit}
                  onChange={(v) => set({ daily_limit: v })}
                />
              </Row>
              <Row label="Pictures per month">
                <Num
                  id="monthly-limit"
                  value={cfg.monthly_limit}
                  onChange={(v) => set({ monthly_limit: v })}
                />
              </Row>
              <Row
                label="Customer previews per day"
                hint="Cap on the public visualiser only"
              >
                <Num
                  id="public-limit"
                  value={cfg.public_daily_limit}
                  onChange={(v) => set({ public_daily_limit: v })}
                />
              </Row>
              <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-3" data-testid="ai-usage">
                {[
                  ["Today", usage.today],
                  ["This month", usage.month],
                  ["All time", usage.total],
                ].map(([label, n]) => (
                  <div
                    key={label}
                    className="rounded-lg border border-zinc-800 bg-zinc-950 p-3"
                  >
                    <div className="text-[10px] font-bold uppercase tracking-widest text-zinc-500">
                      {label}
                    </div>
                    <div className="font-mono-data mt-1 text-lg text-zinc-100">
                      {n ?? 0}
                    </div>
                  </div>
                ))}
              </div>
            </section>

            <section
              className="rounded-lg border border-zinc-800 bg-zinc-900 p-5"
              data-testid="ai-rooms"
            >
              <div className="text-xs font-bold uppercase tracking-widest text-zinc-300">
                Rooms customers can pick
              </div>
              <p className="mt-1 text-xs text-zinc-500">
                The wording below is what the model is told to build around the
                stone.
              </p>
              <div className="mt-4 space-y-4">
                {(cfg.rooms || []).map((room, n) => (
                  <div key={room.key} className="space-y-2">
                    <Input
                      data-testid={`ai-room-label-${room.key}`}
                      value={room.label}
                      onChange={(e) =>
                        set({
                          rooms: cfg.rooms.map((r, i) =>
                            i === n ? { ...r, label: e.target.value } : r,
                          ),
                        })
                      }
                      className="h-10 border-zinc-800 bg-zinc-950 text-sm"
                    />
                    <Textarea
                      data-testid={`ai-room-prompt-${room.key}`}
                      rows={2}
                      value={room.prompt}
                      onChange={(e) =>
                        set({
                          rooms: cfg.rooms.map((r, i) =>
                            i === n ? { ...r, prompt: e.target.value } : r,
                          ),
                        })
                      }
                      className="border-zinc-800 bg-zinc-950 text-sm"
                    />
                  </div>
                ))}
              </div>
              <div className="mt-5 space-y-2">
                <div className="text-[10px] font-bold uppercase tracking-widest text-zinc-500">
                  House style note (added to every picture)
                </div>
                <Textarea
                  data-testid="ai-style-note"
                  rows={2}
                  value={cfg.style_note}
                  onChange={(e) => set({ style_note: e.target.value })}
                  placeholder="e.g. Warm neutral palette, oak joinery, matte black tapware"
                  className="border-zinc-800 bg-zinc-950 text-sm"
                />
                <div className="text-[10px] font-bold uppercase tracking-widest text-zinc-500">
                  Small print under customer previews
                </div>
                <Input
                  data-testid="ai-disclaimer"
                  value={cfg.disclaimer}
                  onChange={(e) => set({ disclaimer: e.target.value })}
                  className="h-10 border-zinc-800 bg-zinc-950 text-sm"
                />
              </div>
            </section>

            <div className="flex flex-wrap gap-2">
              <Button
                data-testid="ai-save"
                disabled={saving}
                onClick={save}
                className="h-11 bg-indigo-500 text-xs font-bold uppercase tracking-widest text-white hover:bg-indigo-400"
              >
                <FloppyDisk size={15} weight="bold" className="mr-2" />
                {saving ? "Saving…" : "Save"}
              </Button>
              <Button
                data-testid="ai-test"
                variant="outline"
                disabled={testing || !cfg.enabled}
                onClick={testShot}
                className="h-11 border-zinc-800 bg-zinc-900 text-xs uppercase tracking-widest hover:bg-zinc-800"
              >
                <Sparkle size={15} weight="bold" className="mr-2" />
                {testing ? "Generating…" : "Try one picture"}
              </Button>
            </div>
          </div>

          <div className="rounded-lg border border-zinc-800 bg-zinc-900 p-5">
            <div className="text-xs font-bold uppercase tracking-widest text-zinc-300">
              Test picture
            </div>
            <div className="mt-3 overflow-hidden rounded-lg border border-zinc-800 bg-zinc-950">
              {shot ? (
                <img
                  src={`${API}/ai-images/image/${shot}`}
                  alt="Generated preview"
                  data-testid="ai-test-image"
                  className="w-full object-cover"
                />
              ) : (
                <div className="flex aspect-[4/3] items-center justify-center text-xs text-zinc-600">
                  {testing ? "Painting…" : "Nothing generated yet"}
                </div>
              )}
            </div>
            <p className="mt-3 text-xs text-zinc-500">
              A picture takes 10–40 seconds. Each one is billed to your Emergent
              key, which is why the daily caps are there.
            </p>
          </div>
        </div>
      </main>
    </div>
  );
}
